import {useState} from 'react';
import {ApolloProvider as Provider} from '@apollo/client';
import {ApolloClient, NormalizedCacheObject} from '@apollo/client';
import {ErrorLink} from '@apollo/client/link/error';
import * as Sentry from '@sentry/nextjs';

import {createClient} from '#apollo/client';
import {useOnceEffect, useToastQueue} from '#hook';

// A number of queries/mutations have their own error-handling, and so we should
// not display the global, generic error toast when these requests error.
const blockList = [
  'CreateAccountMutation',
  'CreateAuthenticationTokenMutation',
  'RequestPasswordReset',
  'ResetPassword',
  'UpdateUserProfile',
  'ChangeEmail',
];

export const ApolloProvider: React.FC<{children?: React.ReactNode}> = ({children}) => {
  const [client, setClient] = useState<ApolloClient<NormalizedCacheObject> | null>(null);
  const {addToast} = useToastQueue();

  const handleApolloError: ErrorLink.ErrorHandler = ({
    graphQLErrors,
    networkError,
    operation,
    response,
  }) => {
    if (graphQLErrors) {
      graphQLErrors.forEach(({message, path, extensions}) => {
        const isValidationError =
          (extensions?.error as unknown as Record<string, any>)?.classCode === 'INVALID_INPUT';
        const hasResponseData = !!response?.data;
        const isInBlockList = blockList.includes(operation.operationName);
        const shouldShowError = isInBlockList ? false : !isValidationError;

        // Only notify the user if absolutely no data came back
        if (!response || !hasResponseData) {
          if (shouldShowError) {
            addToast({
              children:
                'An error has occurred. Please try again in a few minutes or contact customer support',
            });
          }

          Sentry.captureException(
            new Error(
              `[GraphQL error]: Message: ${message} | Query: ${operation.operationName} | Path: ${path}`,
            ),
          );
        }
      });
    }

    if (networkError && !graphQLErrors?.length) {
      console.error('[Network error]', networkError);
      addToast({children: 'A network error has occurred'});
    }
  };

  useOnceEffect(() => {
    setClient(createClient({errorHandler: handleApolloError}));
  });

  return client && <Provider client={client}>{children}</Provider>;
};
